import clsx from 'clsx';
import type { ImageProps } from 'next/image';
import Image from 'next/image';
import Link from 'next/link';

import router from 'next/router';
import type { ComponentType } from 'react';
import type { StoreProductWishlistComponent } from '../../../types.d/storeProduct';
import styles from './ArticleCard.module.scss';

export type ArticleCardProps = {
  image?: ImageProps;
  category: string;
  title: string;
  url: string;
  excerpt?: string;
  featured?: boolean;
  isSideRailArticle?: boolean;
  isLoading?: boolean;
  wishlistComponent?: ComponentType<StoreProductWishlistComponent>;
};

/**
 * The primary Article Card component.
 * @param props - The card props.
 */
export const ArticleCard = ({
  image,
  category,
  title,
  url,
  excerpt,
  featured = false,
  isSideRailArticle = false,
  isLoading = false,
  wishlistComponent: WishlistComponent,
}: ArticleCardProps) => {
  const handle = url?.split('/').pop() ?? '';

  const clickHandler = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.preventDefault();
    router.push(url);
  };

  return (
    <div
      className={clsx(styles.articleCard, {
        [styles['articleCard--featured']]: featured,
        grid: featured,
        'is-loading': isLoading,
      })}
    >
      <div
        className={clsx(styles.articleCard__imageContainer, {
          'col xs4 l8': featured,
        })}
      >
        {!isLoading && image && (
          <Image
            className={styles.articleCard__image}
            src={image.src}
            alt={image.alt}
            title={image.title || image.alt}
            fill
            sizes={
              featured
                ? '(min-width: 1440px) 927px, (min-width: 768px) 66vw, 100vw'
                : '(min-width: 1440px) 454px, (min-width: 768px) 33vw, 38vw'
            }
            onClick={clickHandler}
          />
        )}

        {!isLoading && WishlistComponent && (
          <div className={styles.articleCard__wishlistWrapper}>
            <WishlistComponent handle={handle} itemType="articles" />
          </div>
        )}
      </div>

      <Link
        href={url}
        className={clsx(styles.articleCard__content, {
          'col xs4 l4': featured,
        })}
      >
        <p
          className={clsx(styles.articleCard__category, {
            'text-utility-utility-small-mobile text-utility-utility-desktop':
              !isSideRailArticle,
            'text-utility-utility-small-desktop': isSideRailArticle,
          })}
        >
          {category}
        </p>

        <h3
          className={clsx(styles.articleCard__title, {
            'text-blog-heading-h4-desktop': isSideRailArticle,
            'text-blog-heading-h1-mobile text-blog-heading-h1-desktop':
              featured && !isSideRailArticle,
            'text-blog-heading-h4-mobile text-blog-heading-h2-desktop':
              !featured && !isSideRailArticle,
          })}
        >
          {title}
        </h3>

        {/* Loading state for excerpt for featured articles */}
        {featured && isLoading && (
          <p
            className={clsx(
              'text-blog-heading-h3-mobile',
              'text-blog-heading-h3-desktop',
              styles.articleCard__excerpt
            )}
          >
            {excerpt || ''}
          </p>
        )}

        {featured && excerpt && !isLoading && (
          <p
            className={clsx(
              'text-blog-heading-h3-mobile',
              'text-blog-heading-h3-desktop',
              styles.articleCard__excerpt
            )}
          >
            {excerpt}
          </p>
        )}
      </Link>
    </div>
  );
};
